import React, { useMemo, useRef } from 'react';

import { pluralize } from '../../lib/helpers/text.helpers';
import { ConfirmDialog } from '../_core/_ui/confirm-dialog.component';
import { IntlMsg } from '../_core/IntlMsg/intl-msg.component';
import { ageGroupAccessAffectedCount, ageGroupAccessCopy } from './report-card-access.helpers';
import { AgeGroupAccessWrite, ReportCardAvailabilityMessages } from './report-card-access.types';

interface IReportCardAgeGroupAccessDialogProps {
  action: AgeGroupAccessWrite | null;
  ageGroupName: string;
  counts: IReportCardAvailabilityCounts;
  messages: ReportCardAvailabilityMessages;
  nouns: { one: string; many: string };
  onConfirm: () => void;
  onCancel: () => void;
}

export const ReportCardAgeGroupAccessDialog: React.FC<IReportCardAgeGroupAccessDialogProps> = ({
  action,
  ageGroupName,
  counts,
  messages,
  nouns,
  onConfirm,
  onCancel,
}) => {
  const copyByAction = useMemo(() => ageGroupAccessCopy(messages), [messages]);
  // Keeps the last action's copy and count on screen while the dialog fades out; the optimistic
  // reply would otherwise zero the count mid-fade.
  const shown = useRef<{ action: AgeGroupAccessWrite; count: number }>({ action: 'REVOKE', count: 0 });
  if (action) shown.current = { action, count: ageGroupAccessAffectedCount(counts, action) };
  const copy = copyByAction[shown.current.action];
  const { count } = shown.current;
  const values = { ageGroup: ageGroupName, count, players: pluralize(count, nouns.one, nouns.many) };

  return (
    <ConfirmDialog
      open={!!action}
      title={<IntlMsg {...copy.title} values={values} />}
      message={<IntlMsg {...copy.body} values={values} />}
      confirmLabel={<IntlMsg {...copy.button} />}
      cancelLabel={<IntlMsg {...messages.btnCancel} />}
      onConfirm={onConfirm}
      onCancel={onCancel}
      destructive={copy.destructive}
    />
  );
};
